import { Card, CardContent } from "@/shared/ui/card.tsx"
import { Carousel, CarouselContent, CarouselItem } from "@/shared/ui/carousel.tsx"
import Autoplay from "embla-carousel-autoplay"
import { ThemeEvent } from '../../types';
import { cn } from "@/lib/utils";
import { useAtomValue } from "jotai";
import { themeAtom } from "@/features/app-layout/atoms/theme-atoms";

type Props = {
  themes: ThemeEvent[],
  onNavigate: () => void,
  images: string[]
}

const ThemeCarousel = ({ themes, images, onNavigate }: Props) => {
  // Triple the themes array for ultra-smooth infinite scrolling
  const duplicatedThemes = [...themes, ...themes, ...themes];

  const themeApp = useAtomValue(themeAtom)

  return (
    <div 
      data-testid="themes"
      onClick={onNavigate}
      className="flex gap-4 w-full pt-2 cursor-pointer rounded-2xl border-[3px] mt-1 p-1 border-purple-200 overflow-hidden">
      <div className="flex-1 flex flex-col h-40 bg-white relative items-center justify-center gap-2">
        <div
          className={cn(
            "absolute inset-0",
            "[background-size:10px_10px]",
            "[background-image:radial-gradient(#d4d4d4_1px,transparent_1px)]",
            "dark:[background-image:radial-gradient(#404040_1px,transparent_1px)]",
          )}
        />
        {/* Radial gradient for the container to give a faded look */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)] dark:bg-black"></div>
        <div></div>
        <h3 className="text-xl lg:text-3xl font-montserrat tracking-wide font-semibold w-full text-center text-purple-500">siAi Theme Ideas</h3>
        <p className="text-xs text-center md:text-sm font-poppins tracking-wide text-gray-500">See what siAi suggests based on {themeApp.company} voice and virality</p>

      </div>
      <div className="w-1/5 md:w-2/5 flex items-center gap-2 justify-end">
        <Carousel
          opts={{
            align: "start",
            loop: true,
            duration: 20, // Reduced duration for smoother transitions
            skipSnaps: false,
            dragFree: false,
          }}
          plugins={[
            Autoplay({
              delay: 1500, // Reduced delay
              stopOnInteraction: false,
              stopOnMouseEnter: false,
              stopOnFocusIn: false,
            }),
          ]}
          orientation="vertical"
          className="h-40 rotate-180"
        >
          <CarouselContent className="h-36 -mt-4">
            {duplicatedThemes.map((card, index) => (
              <CarouselItem key={`${card.event_name}-${index}`} className="pt-4 basis-1/2 rotate-180">
                <Card className="size-24 md:size-36">
                  <CardContent className="flex items-center justify-center h-full">
                    <div className="flex-shrink-0 h-24 md:h-36 overflow-hidden rounded-lg">
                      <img
                        src={card.image_url || "/placeholder.svg"}
                        className="w-full h-full object-cover transition-transform hover:scale-105"
                      />
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <Carousel
          opts={{
            align: "center",
            loop: true,
            duration: 20, // Reduced duration for smoother transitions
            skipSnaps: false,
            dragFree: false,
          }}
          plugins={[
            Autoplay({
              delay: 1500, // Reduced delay
              stopOnInteraction: false,
              stopOnMouseEnter: false,
              stopOnFocusIn: false,
            }),
          ]}
          orientation="vertical"
          className="h-40 hidden md:block"
        >
          <CarouselContent className="h-36 -mt-4">
            {images.map((card, index) => (
              <CarouselItem key={index} className="pt-4 basis-1/2">
                <Card className="size-36">
                  <CardContent className="flex items-center justify-center h-full">
                    <div className="flex-shrink-0 h-36 overflow-hidden rounded-lg">
                      <img
                        src={card || "/placeholder.svg"}
                        className="w-full h-full object-cover transition-transform hover:scale-105"
                      />
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  );
};

export default ThemeCarousel
